import React from 'react';
import { Button as MuiButton, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';

const StyledButton = styled(MuiButton)(({ theme, customVariant }) => ({
  borderRadius: theme.spacing(1),
  textTransform: 'none',
  fontWeight: 500,
  minHeight: 40,

  // Custom variants
  ...(customVariant === 'gradient' && {
    background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
    color: theme.palette.primary.contrastText,
    boxShadow: `0 3px 5px 2px ${theme.palette.primary.main}30`,
    '&:hover': {
      background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
    },
  }),

  ...(customVariant === 'danger' && {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  }),

  ...(customVariant === 'success' && {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
  }),
}));

/**
 * Enhanced MUI Button component with additional features and consistent styling
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Button content
 * @param {boolean} props.loading - Show loading spinner
 * @param {string} props.customVariant - Custom styling variant
 * @param {boolean} props.disabled - Disabled state
 * @param {function} props.onClick - Click handler
 * @param {string} props.variant - MUI button variant
 * @param {string} props.color - MUI button color
 * @param {string} props.size - MUI button size
 * @param {React.ReactNode} props.startIcon - Icon before text
 * @param {React.ReactNode} props.endIcon - Icon after text
 * @param {boolean} props.fullWidth - Full width button
 */
const Button = ({
  children,
  loading = false,
  customVariant,
  disabled,
  onClick,
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  startIcon,
  endIcon,
  fullWidth = false,
  ...otherProps
}) => {
  const handleClick = (event) => {
    if (loading || disabled) {
      event.preventDefault();
      return;
    }
    if (onClick) {
      onClick(event);
    }
  };

  return (
    <StyledButton
      variant={variant}
      color={color}
      size={size}
      customVariant={customVariant}
      disabled={disabled || loading}
      onClick={handleClick}
      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : startIcon}
      endIcon={endIcon}
      fullWidth={fullWidth}
      {...otherProps}
    >
      {children}
    </StyledButton>
  );
};

Button.propTypes = {
  children: PropTypes.node.isRequired,
  loading: PropTypes.bool,
  customVariant: PropTypes.oneOf(['gradient', 'danger', 'success']),
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  variant: PropTypes.oneOf(['text', 'outlined', 'contained']),
  color: PropTypes.oneOf([
    'inherit',
    'primary',
    'secondary',
    'success',
    'error',
    'info',
    'warning',
  ]),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  fullWidth: PropTypes.bool,
};

export default Button;
